import Layout from "Layout";
import { ArrowLeft, ArrowRight, ArrowRightFromLineIcon, Briefcase, LayoutDashboard, Users } from "lucide-react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import TeamDashboardTabV2 from "./components/TeamDashboardTabV2";
import TeamAccountsTab from "./components/TeamAccountsTab";
import TeamMembersTab from "./components/TeamMembersTab";
import { TeamApi, TeamDetailTab } from "./type";
import "./style/index.scss";
import { getMyTeam, leaveTeam } from "./funtion";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin } from "antd";

const TEAM_DETAIL_TABS: TeamDetailTab[] = ["dashboard", "accounts", "members"];

const TeamDetails: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const teamId = Number(id);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showleaveConfirmModal, setShowLeaveConfirmModal] = useState(false);

  // My teams: used for the role in the current team, the page title and the team switcher.
  const [myTeams, setMyTeams] = useState<TeamApi[] | null>(null);
  useEffect(() => {
    getMyTeam()
      .then(setMyTeams)
      .catch((error) => {
        console.error("Error fetching team role info:", error);
        setMyTeams([]);
      });
  }, []);

  const currentTeam = myTeams?.find((tm) => tm.team_id === teamId);

  // Role is derived (not stored) so it is right immediately after switching to another team.
  // Until the team list arrives, fall back to what the teams list page passed in the router state.
  const role = useMemo(() => {
    if (!myTeams) return null;
    const myId = JSON.parse(localStorage.getItem("user") || "{}").id;
    const me = currentTeam?.members?.find((m) => m.user_id === myId);
    return {
      isAdmin: !!me && (me.role === "Admin" || me.role === "Deputy"),
      canLeave: !!me && me.role !== "Admin",
    };
  }, [myTeams, currentTeam]);
  const isAdmin: boolean | undefined = role ? role.isAdmin : location.state?.isAdmin;
  const canLeave: boolean | undefined = role ? role.canLeave : location.state?.canLeave;

  const requestedTab = TEAM_DETAIL_TABS.includes(searchParams.get("tab") as TeamDetailTab)
    ? (searchParams.get("tab") as TeamDetailTab)
    : null;
  // Opening /teams/:id directly has no router state, so the role arrives a moment later. Wait for it instead of
  // rendering the accounts tab first and then jumping to the dashboard.
  const roleKnown = isAdmin !== undefined;
  const activeTab: TeamDetailTab =
    requestedTab === "dashboard" && isAdmin === false
      ? "accounts"
      : requestedTab ?? (isAdmin ? "dashboard" : "accounts");
  const waitingForRole = !roleKnown && (requestedTab === null || requestedTab === "dashboard");

  const handleTabChange = (tab: TeamDetailTab) => {

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    }, { state: { isAdmin, canLeave } });
  };

  return (
    <Layout>
      <>
        <Helmet>
          <title>
            {`${currentTeam?.team_name ? `${currentTeam.team_name} · ` : ""}${t("Teams")} | LUMIR`}
          </title>
        </Helmet>
        {/* Clear way back to the list + quick switch to another team (the old breadcrumb link was tiny and grey). */}
        <div className="team-detail-head flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate("/teams")}
              className="team-detail-head__back"
              title={t("BackToTeams")}
            >
              <ArrowLeft size={16} />
              <span>{t("Teams_dashboard.navigation.teams")}</span>
            </button>
            <span className="team-detail-head__sep">/</span>
            {myTeams && myTeams.length > 1 ? (
              <Select
                className="team-detail-head__switch"
                value={currentTeam ? teamId : undefined}
                placeholder={t("Teams_dashboard.navigation.detail_team")}
                onChange={(v: number) => v !== teamId && navigate(`/teams/${v}`)}
                options={myTeams.map((tm) => ({ value: tm.team_id, label: tm.team_name }))}
                showSearch
                optionFilterProp="label"
                variant="borderless"
                popupMatchSelectWidth={false}
                aria-label={t("SwitchTeam")}
              />
            ) : (
              <span className="team-detail-head__name">
                {currentTeam?.team_name || t("Teams_dashboard.navigation.detail_team")}
              </span>
            )}
          </div>
          { canLeave && (
            <button type="button" className="team-detail-head__leave flex shrink-0 items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2.5 !text-xs sm:!text-sm whitespace-nowrap !text-red-600 border border-red-600 rounded hover:bg-red-50 transition-colors hover:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              title={t('13W_Dashboard.LeaveTeam')}
              aria-label={t('13W_Dashboard.LeaveTeam')}
              onClick={async () => {
                setShowLeaveConfirmModal(true);
              }}>
              <ArrowRightFromLineIcon size={16} />
              <span className="team-detail-head__leave-text">{t('13W_Dashboard.LeaveTeam')}</span>
            </button>
          )}
        </div>

        <div className="team-detail-tabs flex mb-4 overflow-x-auto no-scrollbar">
          {
            isAdmin && (
              <button
                type="button"
                onClick={() => handleTabChange("dashboard")}
                className={`team-detail-tabs__tab flex items-center gap-1.5 px-2.5 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium whitespace-nowrap transition-colors border-transparent ${activeTab === "dashboard" ? "team-detail-tabs__tab--active" : ""
                  }`}
              >
                <LayoutDashboard size={16} className="team-detail-tabs__icon" />
                {t("Dashboard")}
              </button>
            )
          }
          <button
            type="button"
            onClick={() => handleTabChange("accounts")}
            className={`team-detail-tabs__tab flex items-center gap-1.5 px-2.5 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium whitespace-nowrap transition-colors border-transparent ${!waitingForRole && activeTab === "accounts" ? "team-detail-tabs__tab--active" : ""
              }`}
          >
            <Briefcase size={16} className="team-detail-tabs__icon" />
            <span className="team-detail-tabs__label--short">{t("TradingAccounts")}</span>
            <span className="team-detail-tabs__label--long">{t("TradingAccountsList")}</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("members")}
            className={`team-detail-tabs__tab flex items-center gap-1.5 px-2.5 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium whitespace-nowrap transition-colors border-transparent ${!waitingForRole && activeTab === "members" ? "team-detail-tabs__tab--active" : ""
              }`}
          >
            <Users size={16} className="team-detail-tabs__icon" />
            <span className="team-detail-tabs__label--short">{t("Members")}</span>
            <span className="team-detail-tabs__label--long">{t("TeamMembersList")}</span>
          </button>
        </div>
        <Modal
          title={t("13W_Dashboard.LeaveTeamConfirm_title")}
          open={showleaveConfirmModal}
          onCancel={() => setShowLeaveConfirmModal(false)}
          footer={null}
          centered
          className="team-members-tab__confirm-modal"
        >
          <p className="team-members-tab__confirm-text text-sm mb-4">
            {t("13W_Dashboard.LeaveTeamConfirm_message")}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowLeaveConfirmModal(false)}
              className="team-members-tab__confirm-btn team-members-tab__confirm-btn--cancel px-4 py-2 rounded-md text-sm"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={
                async () => {
                  if (teamId) {
                    // leaveTeam already toasts on network/HTTP errors.
                    const data = await leaveTeam(teamId).catch(() => null);
                    if (!data) return;
                    if (data.success) {
                      setShowLeaveConfirmModal(false);
                      toast.success(t("LeaveTeamSuccess"));
                      navigate("/teams", { replace: true });
                    } else {
                      toast.error(data?.message || t("SomethingWentWrong"));
                    }
                  }
                }
              }
              className={`team-members-tab__confirm-btn px-4 py-2 rounded-md text-sm text-white team-members-tab__confirm-btn--reject`}
            >
              {t("13W_Dashboard.confirm")}
            </button>
          </div>
        </Modal>


        {waitingForRole ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : (
          // Keyed by team so every tab reloads its data after switching teams (they fetch on mount only).
          <div key={teamId}>
            {activeTab === "dashboard" && isAdmin && <TeamDashboardTabV2 />}
            {activeTab === "accounts" && <TeamAccountsTab />}
            {activeTab === "members" && <TeamMembersTab />}
          </div>
        )}

      </>
    </Layout>
  );
};

export default TeamDetails;
